import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { 
  History,
  ArrowLeft,
  CheckCircle,
  Clock,
  AlertCircle,
  Send as SendIcon,
  Users,
  Calendar,
  Mail,
  Timer
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

// Countdown component for scheduled sends (debounced onExpire)
const Countdown = ({ scheduledFor, onExpire }) => {
  const { t, i18n } = useTranslation();
  const [timeLeft, setTimeLeft] = useState('');
  const expiredRef = React.useRef(false);

  useEffect(() => {
    let intervalId;
    const calculateTimeLeft = () => {
      const now = new Date();
      const target = new Date(scheduledFor);
      const diff = target - now;

      if (diff <= 0) {
        setTimeLeft(t('sendmirein.history.sendingNow'));
        if (!expiredRef.current) {
          expiredRef.current = true;
          if (onExpire) onExpire();
        }
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      let parts = [];
      if (days > 0) parts.push(`${days}T`);
      if (hours > 0 || days > 0) parts.push(`${hours}h`);
      if (minutes > 0 || hours > 0 || days > 0) parts.push(`${minutes}m`);
      parts.push(`${seconds}s`);

      setTimeLeft(parts.join(' '));
    };

    calculateTimeLeft();
    intervalId = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(intervalId);
  }, [scheduledFor, onExpire]);

  return (
    <div className="flex items-center gap-2 text-sm font-medium text-orange-600">
      <Timer className="w-4 h-4 animate-pulse" />
      <span>{timeLeft}</span>
    </div>
  );
};

const SendmireinHistory = () => {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [sends, setSends] = useState([]);
  const [filter, setFilter] = useState('ALL'); // ALL, SENT, PENDING, FAILED
  const [statsMap, setStatsMap] = useState({}); // id -> { success, errors, total }
  const [statsBackoffUntil, setStatsBackoffUntil] = useState(0);

  useEffect(() => {
    if (token) {
      loadSends();
    }
  }, [token]);

  const loadSends = async () => {
    try {
      setLoading(true);
      const data = await api.newslettersSendsList(token);
      setSends(data);
      // Reduce API pressure: only fetch stats for priority sends (SENDING)
      const now = Date.now();
      if (now < statsBackoffUntil) return; // backoff after 429
      const sendingIds = (data || []).filter(s => s.status === 'SENDING').map(s => s.id);
      const ids = Array.from(new Set([...sendingIds]));
      const nextMap = { ...statsMap };
      for (const id of ids) {
        try {
          const logs = await api.newslettersGetSendLogs(token, id, 1, 1);
          nextMap[id] = { id, success: logs.success || 0, errors: logs.errors || 0, total: logs.total || 0 };
          // small delay to avoid rate limits
          await new Promise(r => setTimeout(r, 120));
        } catch (e) {
          // On 429, back off for 10s
          setStatsBackoffUntil(Date.now() + 10000);
          break;
        }
      }
      setStatsMap(nextMap);
    } catch (error) {
      console.error('Error loading send history:', error);
      toast({
        title: t('sendmirein.common.error'),
        description: t('sendmirein.history.loadError'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Lightweight refresh for a single send card without reloading the full list
  const refreshSendById = async (sendId) => {
    try {
      const updated = await api.newslettersGetSend(token, sendId);
      setSends((prev) => prev.map((s) => (s.id === sendId ? { ...s, ...updated } : s)));
      // Also refresh stats for this send only
      try {
        const logs = await api.newslettersGetSendLogs(token, sendId, 1, 1);
        setStatsMap((prev) => ({
          ...prev,
          [sendId]: {
            id: sendId,
            success: logs.success || 0,
            errors: logs.errors || 0,
            total: logs.total || 0,
          },
        }));
      } catch (e) {
        // ignore stats errors for single refresh to keep it light
      }
      return updated;
    } catch (error) {
      console.error('Error refreshing send card:', error);
      return null;
    }
  };

  // After countdown expiry: refresh only this card every 5s until it leaves PENDING or max tries reached
  const startExpiryRefreshLoop = async (sendId, maxTries = 12) => {
    let tries = 0;
    const tick = async () => {
      tries += 1;
      const updated = await refreshSendById(sendId);
      const status = updated?.status;
      if (!status || status !== 'PENDING' || tries >= maxTries) {
        return; // stop when status changes or after maxTries (~1 minute)
      }
      setTimeout(tick, 5000);
    };
    // kick off immediately
    setTimeout(tick, 0);
  };

  const handleCancelSend = async (sendId) => {
    if (!confirm(t('sendmirein.history.confirmCancelSend'))) return;

    try {
      await api.newslettersCancelSend(token, sendId);
      toast({
        title: t('sendmirein.common.success'),
        description: t('sendmirein.history.canceledDesc'),
      });
      loadSends();
    } catch (error) {
      console.error('Error cancelling send:', error);
      toast({
        title: t('sendmirein.common.error'),
        description: t('sendmirein.history.cancelFailed'),
        variant: 'destructive',
      });
    }
  };

  const filteredSends = sends.filter((send) => {
    if (filter === 'ALL') return true;
    return send.status === filter;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'SENT':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'PENDING':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'SENDING':
        return <SendIcon className="w-5 h-5 text-blue-600 animate-pulse" />;
      case 'FAILED':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      case 'CANCELLED':
        return <AlertCircle className="w-5 h-5 text-muted-foreground" />;
      default:
        return <Clock className="w-5 h-5 text-muted-foreground" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'SENT':
        return t('sendmirein.history.status.sent');
      case 'PENDING':
        return t('sendmirein.history.status.pending');
      case 'SENDING':
        return t('sendmirein.history.status.sending');
      case 'FAILED':
        return t('sendmirein.history.status.failed');
      case 'CANCELLED':
        return t('sendmirein.history.status.canceled');
      default:
        return status;
    }
  };

  // Compute ETA for ongoing sends using simple throughput assumptions
  function computeEta(send) {
    const stats = statsMap[send.id] || { success: 0, errors: 0, total: 0 };
    const done = stats.success + stats.errors;
    const total = send.recipientCount || stats.total || 0;
    const remaining = Math.max(0, total - done);
    // Match backend: BATCH_SIZE=10, DELAY_EMAIL=200ms, DELAY_BATCH=2000ms
    const perEmailMs = 200;
    const perBatchMs = 2000;
    const batchSize = 10;
    const fullBatches = Math.floor(remaining / batchSize);
    const leftover = remaining % batchSize;
    const estimateMs = fullBatches * (perBatchMs + batchSize * perEmailMs) + leftover * perEmailMs;
    const minutes = Math.floor(estimateMs / 60000);
    const seconds = Math.floor((estimateMs % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'SENT':
        return 'bg-green-100 text-green-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'SENDING':
        return 'bg-blue-100 text-blue-800';
      case 'FAILED':
        return 'bg-red-100 text-red-800';
      case 'CANCELLED':
        return 'bg-muted text-foreground';
      default:
        return 'bg-muted text-foreground';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const locale = i18n?.language || 'de-DE';
    return date.toLocaleString(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-muted p-6">
        <div className="max-w-7xl mx-auto">
          <p className="text-center text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
            <History className="w-8 h-8" />
            {t('sendmirein.history.title')}
          </h1>
          <p className="text-muted-foreground mt-1">{t('sendmirein.history.subtitle')}</p>
        </div>
        {/* Back Button */}
        <Button
          onClick={() => navigate('/sendmirein')}
          variant="outline"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('sendmirein.history.backToDashboard')}
        </Button>

        {/* Filter Buttons */}
        <Card className="p-4">
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={() => setFilter('ALL')}
              variant={filter === 'ALL' ? 'default' : 'outline'}
              size="sm"
            >
              {t('sendmirein.history.filters.all', { count: sends.length })}
            </Button>
            <Button
              onClick={() => setFilter('SENT')}
              variant={filter === 'SENT' ? 'default' : 'outline'}
              size="sm"
            >
              {t('sendmirein.history.filters.sent', { count: sends.filter(s => s.status === 'SENT').length })}
            </Button>
            <Button
              onClick={() => setFilter('PENDING')}
              variant={filter === 'PENDING' ? 'default' : 'outline'}
              size="sm"
            >
              {t('sendmirein.history.filters.pending', { count: sends.filter(s => s.status === 'PENDING').length })}
            </Button>
            <Button
              onClick={() => setFilter('SENDING')}
              variant={filter === 'SENDING' ? 'default' : 'outline'}
              size="sm"
            >
              {t('sendmirein.history.filters.sending', { count: sends.filter(s => s.status === 'SENDING').length })}
            </Button>
            <Button
              onClick={() => setFilter('FAILED')}
              variant={filter === 'FAILED' ? 'default' : 'outline'}
              size="sm"
            >
              {t('sendmirein.history.filters.failed', { count: sends.filter(s => s.status === 'FAILED').length })}
            </Button>
          </div>
        </Card>

        {/* Send History List */}
        <Card className="p-6">
          {filteredSends.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <History className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p>{t('sendmirein.history.none')}</p>
              {filter !== 'ALL' && (
                <p className="text-sm mt-2">{t('sendmirein.history.adjustFilter')}</p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredSends.map((send) => (
                <Card key={send.id} className="p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="mt-1">
                        {getStatusIcon(send.status)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-semibold text-base truncate">
                            {send.subject}
                          </h4>
                          <span className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${getStatusColor(send.status)}`}>
                            {getStatusText(send.status)}
                            {send.status === 'SENT' && statsMap[send.id] && (
                              <> ({t('sendmirein.history.successCount', { success: statsMap[send.id].success, total: (send.recipientCount || statsMap[send.id].total) })})</>
                            )}
                            {send.status === 'SENDING' && statsMap[send.id] && (
                              <> ({(statsMap[send.id].success + statsMap[send.id].errors)}/{send.recipientCount || statsMap[send.id].total}) · {t('sendmirein.history.eta')} {computeEta(send)}</>
                            )}
                          </span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            <span>{t('sendmirein.history.recipients', { count: (send.recipientCount || 0) })}</span>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span>
                              {send.scheduledFor 
                                ? t('sendmirein.history.scheduled', { date: formatDate(send.scheduledFor) })
                                : t('sendmirein.history.now')
                              }
                            </span>
                          </div>

                          {send.status === 'PENDING' && send.scheduledFor && (
                            <div className="col-span-full">
                              <Countdown 
                                scheduledFor={send.scheduledFor}
                                onExpire={() => {
                                  // After scheduled time hit, start a 5s refresh loop for this card
                                  setTimeout(() => startExpiryRefreshLoop(send.id), 2000);
                                }}
                              />
                            </div>
                          )}

                          {send.sentAt && (
                            <div className="flex items-center gap-2">
                              <SendIcon className="w-4 h-4" />
                              <span>{t('sendmirein.history.sentAt', { date: formatDate(send.sentAt) })}</span>
                            </div>
                          )}
                          {send.status === 'SENDING' && statsMap[send.id] && (
                            <div className="flex items-center gap-2">
                              <SendIcon className="w-4 h-4 animate-pulse" />
                              <span>
                                {t('sendmirein.history.progress', { done: (statsMap[send.id].success + statsMap[send.id].errors), total: (send.recipientCount || statsMap[send.id].total) })} · ETA {computeEta(send)}
                              </span>
                            </div>
                          )}
                        </div>

                        {send.errorMessage && (
                          <div className="mt-3 p-3 bg-red-50 rounded text-sm text-red-800">
                            <strong>{t('sendmirein.common.error')}:</strong> {send.errorMessage}
                          </div>
                        )}

                        {send.template && (
                          <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {t('sendmirein.history.template')}: {send.template.name}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      {send.status === 'PENDING' && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleCancelSend(send.id)}
                        >
                          {t('sendmirein.common.cancel')}
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/sendmirein/history/${send.id}`)}
                      >
                        {t('sendmirein.common.details')}
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default SendmireinHistory;
